/**
 * One readable zh sentence per audit row (總覽「最近活動」, 稽核日誌「對象與內容」, 查一台機器「紀錄」),
 * built from the row's before / after / metadata instead of listing raw field names. Licence-recovery
 * keys (code_prefix / code_sha256) are never shown — the Worker already redacts them, and no sentence
 * reads them. An action without a sentence falls back to its action id.
 */
import type { AuditEntry } from '@/types';
import { fmtDay } from './format';
import { IMPORT_FIELD_LABEL, ROLE_LABEL_ZH, ticketStatusLabel } from './labels';

type Obj = Record<string, unknown>;
type Row = Pick<AuditEntry, 'action' | 'target_id' | 'target_collection' | 'before' | 'after' | 'metadata'>;

const ADMIN_STATUS_LABEL: Record<string, string> = { pending: '待審核', approved: '已核准', revoked: '已撤銷' };

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const day = (v: unknown): string => {
  if (typeof v === 'number') return fmtDay(v);
  return str(v) ?? '無';
};
const shown = (v: unknown): string => (v === null || v === undefined || v === '' ? '(空)' : `「${String(v)}」`);

/** 「擁有人「A」→「B」、團隊「(空)」→「業務」」 for the changed owner fields (timestamps skipped). */
function fieldChanges(before: Obj | null, after: Obj | null, max = 3): string {
  const keys = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])).filter(
    (k) => k !== 'sn' && k in IMPORT_FIELD_LABEL && JSON.stringify(before?.[k] ?? null) !== JSON.stringify(after?.[k] ?? null),
  );
  const parts = keys.slice(0, max).map((k) =>
    k === 'expires_at' ? `到期日 ${day(before?.[k])} → ${day(after?.[k])}` : `${IMPORT_FIELD_LABEL[k]} ${shown(before?.[k])} → ${shown(after?.[k])}`,
  );
  if (keys.length > max) parts.push(`等 ${keys.length} 項`);
  return parts.join('、');
}

/** The fields an import row provided (after only), as labels. */
function providedFields(after: Obj | null): string {
  return Object.keys(after ?? {})
    .filter((k) => k !== 'sn' && k in IMPORT_FIELD_LABEL)
    .map((k) => IMPORT_FIELD_LABEL[k])
    .join('、');
}

/** The sentence. `withTarget: false` drops the target (the lookup drawer is already about that SN). */
export function auditSentence(e: Row, opts: { withTarget?: boolean } = {}): string {
  const withTarget = opts.withTarget ?? true;
  const before = (e.before ?? null) as Obj | null;
  const after = (e.after ?? null) as Obj | null;
  const meta = (e.metadata ?? null) as Obj | null;
  const t = withTarget ? (e.target_id ?? '') : '';
  const sp = (s: string) => (s ? ` ${s}` : '');
  const reason = str(meta?.reason);
  const why = reason ? `(原因:${reason})` : '';

  switch (e.action) {
    case 'whitelist.add': {
      const exp = num(after?.expires_at);
      return `手動加入名單${sp(t)}${exp ? `,到期 ${fmtDay(exp)}` : ''}`;
    }
    case 'whitelist.revoke':
      return `撤銷${sp(t)}${why}${meta?.bulk ? ',批次撤銷' : ''}`;
    case 'whitelist.restore':
      return `復原${sp(t)},重新加入名單`;
    case 'whitelist.meta_update': {
      const c = fieldChanges(before, after);
      return `更新${sp(t)}${c ? `:${c}` : '的資料'}`;
    }
    case 'whitelist.expiry':
      return `${t ? `${t} ` : ''}到期日 ${day(before?.expires_at)} → ${day(after?.expires_at)}`;
    case 'whitelist.import_add': {
      const f = providedFields(after);
      return `匯入新增${sp(t)}${f ? `(含${f})` : ''}`;
    }
    case 'whitelist.import_update': {
      const c = fieldChanges(before, { ...(before ?? {}), ...(after ?? {}) });
      return `匯入更新${sp(t)}${c ? `:${c}` : ''}`;
    }
    case 'whitelist.import_restore':
      return `匯入時復原${sp(t)},重新加入名單`;
    case 'whitelist.import_commit': {
      const name = str(after?.name);
      const parts = [`新增 ${num(after?.added) ?? 0}`, `更新 ${num(after?.updated) ?? 0}`];
      if (num(after?.restored)) parts.push(`復原 ${num(after?.restored)}`);
      parts.push(`略過 ${num(after?.skipped) ?? 0}`);
      return `匯入批次${name ? `「${name}」` : ''}完成:${parts.join('、')}`;
    }
    case 'whitelist.import_expired':
      return '匯入批次未完成且暫存已過期,標為已失效';
    case 'machine.approve':
      return `核准機器${sp(t)},加入名單`;
    case 'machine.reject':
      return `拒絕機器${sp(t)}`;
    case 'license.activate':
      return `兌換軟體序號${t ? `,${t} 加入名單` : ',加入名單'}`;
    case 'license.repair_admission':
      return `以已綁定的序號找回授權${t ? `,${t} 回到名單` : ',回到名單'}`;
    case 'license.mint': {
      const q = num(meta?.quantity);
      const partner = str(meta?.partner);
      return `鑄造 ${q ?? '?'} 組軟體序號${partner ? `(${partner})` : ''}`;
    }
    case 'license.revoke':
      return '撤銷一組軟體序號';
    case 'model_whitelist.add':
      return `新增機型 ${str(after?.display_model) ?? e.target_id ?? ''}`.trim();
    case 'model_whitelist.update':
      return `更新機型 ${str(after?.display_model) ?? e.target_id ?? ''}`.trim();
    case 'model_whitelist.revoke':
      return `撤銷機型${sp(e.target_id ?? '')}`;
    case 'model_whitelist.restore':
      return `復原機型${sp(e.target_id ?? '')}`;
    case 'support.ticket_create': {
      const subject = str(after?.subject);
      const n = num(after?.attachment_count);
      return `新工單${subject ? `「${subject}」` : ''}${n ? `,附件 ${n} 個` : ''}`;
    }
    case 'support.message_append':
      return '使用者補充訊息';
    case 'support.ticket.status':
      return `工單狀態 ${ticketStatusLabel(String(before?.status ?? '—'))} → ${ticketStatusLabel(String(after?.status ?? '—'))}`;
    case 'support.ticket.assignee': {
      const a = str(after?.assignee);
      return a ? `工單指派給 ${a}` : '取消工單指派';
    }
    case 'support.ticket.reply':
      return '回覆使用者(經 Email 寄出)';
    case 'support.ticket.note':
      return '新增內部備註';
    case 'admin_user.set_role': {
      const role = (v: unknown) => ROLE_LABEL_ZH[String(v) as keyof typeof ROLE_LABEL_ZH] ?? (v ? String(v) : '無');
      const st = (v: unknown) => ADMIN_STATUS_LABEL[String(v)] ?? (v ? String(v) : '無');
      const parts: string[] = [];
      if (before?.role !== after?.role) parts.push(`角色 ${role(before?.role)} → ${role(after?.role)}`);
      if (before?.status !== after?.status) parts.push(`狀態 ${st(before?.status)} → ${st(after?.status)}`);
      return `${t ? `${t} ` : ''}${parts.join('、') || '帳號設定未變'}`;
    }
    case 'metrics.aggregate':
      return `每日彙整${sp(t)}`;
    case 'retention.prune':
      return '保留期清理(過期的裝置與暫存資料)';
    // The target is the device id itself — the actor column already names the device.
    case 'device.register':
      return '裝置首次連線';
    case 'device.refresh_rotate':
      return '裝置重新註冊,換發連線憑證';
    case 'device.token_refresh':
      return '裝置更新連線憑證';
    default:
      return [e.action || '—', t].filter(Boolean).join(' · ');
  }
}

/**
 * Routine rows: app-api writes one on every App sign-in / token refresh, so they would bury the
 * operator's own actions. Same set as the cron's DEVICE_AUDIT_ACTIONS (180-day prune).
 */
export const ROUTINE_AUDIT_ACTIONS: ReadonlySet<string> = new Set(['device.register', 'device.refresh_rotate', 'device.token_refresh']);

export function isRoutineAudit(e: Pick<AuditEntry, 'action'>): boolean {
  return ROUTINE_AUDIT_ACTIONS.has(e.action);
}

/** Who did it, short enough for one cell; `title` carries the full id when it was shortened. */
export function auditActor(e: Pick<AuditEntry, 'actor_type' | 'actor_id'>): { text: string; title?: string } {
  const id = e.actor_id?.trim() || '';
  if (e.actor_type === 'app') return id ? { text: `App 裝置 · ${id.slice(-6)}`, title: id } : { text: 'App 裝置' };
  if (e.actor_type === 'system') return { text: id || 'cron' };
  return { text: id || '—', title: id || undefined };
}
